import { NetworkChip } from '@mdvip/shared/components/blocks/NetworkChip';
import {
  ResponsiveModal,
  ResponsiveModalBody,
  ResponsiveModalHeader,
  ResponsiveModalTitle,
} from '@mdvip/shared/components/blocks/ResponsiveModal';
import { useState } from 'react';
import { FormProvider, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMemberAutoPaySummary } from '@/hooks/payments/autoPay/useMemberAutoPaySummary';
import { useEditCardForm } from '@/hooks/payments/creditCard/useEditCardForm';
import { useEditCreditCard } from '@/hooks/payments/creditCard/useEditCreditCard';
import type { CreditCardAccount } from '@/lib/payments/creditCardAccount.types';
import { formatExpiry, isNotPast, toSavedAddress } from '@/lib/payments/paymentAccount.format';
import type { BillingAddressValues } from '@/lib/payments/paymentAccount.types';
import { BillingInformationSection } from './BillingInformationSection';
import { EditModalFooter } from './EditModalFooter';
import { EditReviewScreen } from './EditReviewScreen';
import { AutoPayCheckbox, HsaRadioField, MakeDefaultCheckbox, TextField } from './fields';

interface EditCreditCardModalProps {
  /** The card being edited; `null` closes the modal. Controlled like `RemoveCardDialog`. */
  card: CreditCardAccount | null;
  onClose: () => void;
  /** Called after a successful save — the caller decides toast/close, same contract as Add. */
  onSuccess?: () => void;
}

/**
 * Edit Payment Account modal — credit card. Reuses the Add modal's shared
 * components (fields.tsx, BillingInformationSection, EditModalFooter, the review-screen layout)
 * and screen-switch structure. Sibling to `EditBankAccountModal` — the form body (fields, the
 * expiry-past-lock behavior) stays separate since it's genuinely card-specific, but everything
 * that's identical between the two (billing-address seeding, the footer) is shared.
 *
 * The outer/inner split lets the `ResponsiveModal` shell stay mounted (for exit animation) while
 * `useEditCardForm` and friends only run once a card is selected — calling those hooks
 * unconditionally in a component that never renders without a card. Keying the inner component
 * on `card.id` also means each edit session starts from a clean, freshly-seeded form: no
 * open/close reset plumbing needed, unlike the Add modal (which stays mounted across opens).
 */
export function EditCreditCardModal({ card, onClose, onSuccess }: EditCreditCardModalProps) {
  return (
    <ResponsiveModal open={card !== null} onOpenChange={(open) => !open && onClose()} className="sm:max-w-2xl">
      {card && <EditCreditCardModalContent key={card.id} card={card} onClose={onClose} onSuccess={onSuccess} />}
    </ResponsiveModal>
  );
}

interface EditCreditCardModalContentProps {
  card: CreditCardAccount;
  onClose: () => void;
  onSuccess?: () => void;
}

function EditCreditCardModalContent({ card, onClose, onSuccess }: EditCreditCardModalContentProps) {
  const { t } = useTranslation('members');
  const [screen, setScreen] = useState<'form' | 'confirm'>('form');
  const [billingFormOpen, setBillingFormOpen] = useState(false);

  const savedAddress: BillingAddressValues = toSavedAddress(card);
  const form = useEditCardForm({ card, billingRequired: billingFormOpen });
  const { data: autoPaySummary, isLoading: isAutoPaySummaryLoading } = useMemberAutoPaySummary();
  const cardMutation = useEditCreditCard({ onSuccess });
  const isSubmitting = cardMutation.isPending;

  // Legacy locks HSA, Default, and the billing Change button whenever the currently-typed expiry
  // is in the past — submission is blocked anyway, so the rest of the form goes inert until the
  // member fixes it. Reactive to the live field, not just the card's original (possibly already
  // expired) value: correcting the expiry immediately re-enables the rest of the form.
  const expiryValue = useWatch({ control: form.control, name: 'card.expiry' });
  const expiryIsPastLocked = /^\d{2}\/\d{2}$/.test(expiryValue) && !isNotPast(expiryValue);

  const handleToggleBillingForm = () => {
    if (billingFormOpen) form.resetField('billing');
    setBillingFormOpen((isOpen) => !isOpen);
  };

  const handleReview = form.handleSubmit(() => setScreen('confirm'));

  // The billing address that will be submitted: the entered sub-form when the member opened
  // "Change", otherwise the card's own saved address (mirrors Add's effectiveBilling). Read with
  // useWatch, not getValues: a render-time getValues isn't reactive, so the React Compiler may
  // cache a stale result.
  const enteredBilling = useWatch({ control: form.control, name: 'billing' });
  const effectiveBilling: BillingAddressValues = billingFormOpen ? enteredBilling : savedAddress;

  const handleConfirmSubmit = () => {
    const values = form.getValues();
    cardMutation.submit({
      id: card.id,
      expiry: values.card.expiry,
      isHsa: values.card.isHsa,
      makeDefault: values.card.makeDefault,
      autoPay: values.card.autoPay,
      billing: effectiveBilling,
    });
  };

  return (
    <FormProvider {...form}>
      <ResponsiveModalHeader>
        <ResponsiveModalTitle>{t('payments.editAccount.title')}</ResponsiveModalTitle>
      </ResponsiveModalHeader>

      <ResponsiveModalBody>
        {screen === 'form' ? (
          <div className="space-y-6">
            <div className="flex items-center gap-3 rounded-lg border bg-muted p-3.5">
              <NetworkChip network={card.network} />
              <div className="min-w-0">
                <div className="truncate text-sm font-semibold text-foreground">
                  {`${card.network} ···· ${card.last4}`}
                </div>
                <div className="text-xs text-muted-foreground">{t('payments.editAccount.cardLabel')}</div>
              </div>
            </div>

            <TextField
              name="card.expiry"
              label={t('payments.addAccount.expiry')}
              inputMode="numeric"
              format={formatExpiry}
            />

            <HsaRadioField scope="card" disabled={expiryIsPastLocked} />
            <div className="space-y-2.5">
              <MakeDefaultCheckbox scope="card" isCurrentDefault={card.isDefault} disabled={expiryIsPastLocked} />
              {/* member.autoPay has no card linkage, so the locked-checked display is global — every
                  card shows it when the member is enrolled at all, not just the actual default one.
                  Harmless since the checkbox is disabled in that state; it never touches Default. */}
              <AutoPayCheckbox
                scope="card"
                autoPayEnrolled={autoPaySummary?.autoPayEnrolled ?? false}
                disabled={expiryIsPastLocked}
              />
            </div>

            <BillingInformationSection
              accountKind="card"
              savedAddress={savedAddress}
              formOpen={billingFormOpen}
              onToggleForm={handleToggleBillingForm}
              addNewLabel={t('payments.editAccount.billing.change')}
              addNewIcon="pencil"
              disabled={expiryIsPastLocked}
            />
          </div>
        ) : (
          <EditReviewScreen
            scope="card"
            icon="creditCard"
            headline={`${card.network} ···· ${card.last4}`}
            caption={`${t('payments.addAccount.reviewScreen.expPrefix')} ${expiryValue}`}
            autoPayEnrolled={autoPaySummary?.autoPayEnrolled ?? false}
            autoPayAmount={autoPaySummary?.autoPayAmount ?? null}
            autoPayPlan={autoPaySummary?.autoPayPlan ?? null}
            billing={effectiveBilling}
          />
        )}
      </ResponsiveModalBody>

      <EditModalFooter
        screen={screen}
        onCancel={onClose}
        onReview={handleReview}
        reviewDisabled={isAutoPaySummaryLoading}
        onMakeChanges={() => setScreen('form')}
        onConfirm={handleConfirmSubmit}
        isSubmitting={isSubmitting}
      />
    </FormProvider>
  );
}
